import { useEffect, useState } from "react";
import { Link } from "react-router";
import { BANK } from "../bank/index.ts";
import { api, type Progress } from "../api.ts";
import { usePageMeta } from "../components/Layout.tsx";
import { useSession } from "../session.tsx";
import { formatDue } from "../scheduler.ts";

/**
 * The whole bank, listed. Public on purpose: someone deciding whether to sign up
 * should be able to see exactly what they are getting, and the list being short
 * is a fact about the product rather than something to hide behind a login.
 *
 * Your own history only loads when signed in.
 *
 * Every title links straight to that problem. The scheduler is still what runs a
 * normal session, and picking is not a way around it: the server dates an early
 * rep from what it already knows, so choosing something you do not owe moves its
 * interval half as far and says so on the screen. What picking is for is the
 * session with a reason behind it, revising one pattern before an interview,
 * which is worth more than protecting people from their own taste.
 */
export function Problems() {
  usePageMeta("Problems", "Every problem in the bank, with the pattern it trains.");
  const { user } = useSession();
  const [progress, setProgress] = useState<Map<string, Progress>>(new Map());

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    api
      .state()
      .then(({ progress: rows }) => {
        if (!cancelled) setProgress(new Map(rows.map((r) => [r.drillId, r])));
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [user]);

  const now = new Date();

  return (
    <article className="prose-page">
      <h1>Problems</h1>
      <p className="lede">
        {BANK.length} {BANK.length === 1 ? "problem" : "problems"} across{" "}
        {new Set(BANK.map((d) => d.pattern)).size}{" "}
        {new Set(BANK.map((d) => d.pattern)).size === 1 ? "pattern" : "patterns"}. The list is short by
        design and grows when a problem earns its place.
      </p>

      <div className="table-scroll">
        <table className="board">
          <thead>
            <tr>
              <th>Problem</th>
              <th>Pattern</th>
              <th>Difficulty</th>
              <th className="num">Budget</th>
              {user && <th className="num">Your history</th>}
            </tr>
          </thead>
          <tbody>
            {BANK.map((drill) => {
              const seen = progress.get(drill.id);
              return (
                <tr key={drill.id}>
                  <td>
                    <Link className="pick" to={`/drill/${drill.id}`}>
                      {drill.title}
                    </Link>
                  </td>
                  <td>
                    <span className="chip">{drill.pattern}</span>
                  </td>
                  <td>{drill.difficulty}</td>
                  <td className="num">{Math.round(drill.timeBudgetMs / 60000)} min</td>
                  {user && (
                    <td className="num muted">
                      {seen
                        ? `${seen.passes}/${seen.attempts} · ${formatDue(seen.dueAt, now)}`
                        : "not tried"}
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <p className="muted small">
        Pick any problem to jump straight to it. Anything you are not due yet counts as an early rep
        and moves its interval half as far, so the schedule survives being ignored.{" "}
        {user ? (
          <Link to="/drill">Or let the scheduler choose</Link>
        ) : (
          <Link to="/signup">Create an account</Link>
        )}
        .
      </p>
    </article>
  );
}
